import { BookOpen, ClipboardCheck, RotateCcw, Spade, Zap } from 'lucide-react';
import { painelInsetCardClass } from '@/components/painel/painel-styles';
import {
  formatActivityAction,
  formatActivitySummary,
  formatActivityTimestamp,
  type PainelActivityAction,
  type PainelActivityEntry,
  type PainelActivityResource,
} from '@/lib/painel-activity-log';
import { cn } from '@/lib/utils';

export const PAINEL_ACTIVITY_RESOURCE_META: Record<
  PainelActivityResource,
  { icon: typeof BookOpen; accent: string; label: string }
> = {
  guide: {
    icon: BookOpen,
    accent: 'bg-sky-500/15 text-sky-300 ring-sky-400/25',
    label: 'Guia',
  },
  power: {
    icon: Zap,
    accent: 'bg-amber-500/15 text-amber-300 ring-amber-400/25',
    label: 'Power',
  },
  ace: {
    icon: Spade,
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-400/25',
    label: 'Ace',
  },
};

const ACTION_META: Partial<
  Record<PainelActivityAction, { icon: typeof BookOpen; accent: string }>
> = {
  submit_review: {
    icon: RotateCcw,
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-400/25',
  },
  approve: {
    icon: ClipboardCheck,
    accent: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/25',
  },
};

type PainelActivityTimelineItemProps = {
  entry: PainelActivityEntry;
  variant?: 'user' | 'resource';
};

export function PainelActivityTimelineItem({
  entry,
  variant = 'user',
}: PainelActivityTimelineItemProps) {
  const meta = PAINEL_ACTIVITY_RESOURCE_META[entry.resource];
  const actionMeta = ACTION_META[entry.action];
  const Icon = actionMeta?.icon ?? meta.icon;
  const accent = actionMeta?.accent ?? meta.accent;
  const title =
    variant === 'resource' ? formatActivityAction(entry) : formatActivitySummary(entry);

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      <div className="relative flex flex-col items-center">
        <span
          className={cn(
            'relative z-10 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1',
            accent
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <span className="absolute top-9 bottom-0 w-px bg-gradient-to-b from-white/15 to-transparent" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="font-inter text-sm leading-snug text-slate-100">{title}</p>
        {variant === 'resource' ? (
          <p className="mt-1 font-inter text-xs text-slate-400">
            por {entry.userName}
            <span className="text-slate-600"> · </span>@{entry.username}
          </p>
        ) : null}
        <p className="mt-1 font-inter text-xs text-slate-500">
          {formatActivityTimestamp(entry.timestamp)}
        </p>
      </div>
    </li>
  );
}

type PainelActivityTimelineProps = {
  entries: PainelActivityEntry[];
  variant?: 'user' | 'resource';
  emptyMessage?: string;
  className?: string;
};

export function PainelActivityTimeline({
  entries,
  variant = 'user',
  emptyMessage = 'Nenhuma alteração registada ainda.',
  className,
}: PainelActivityTimelineProps) {
  if (entries.length === 0) {
    return (
      <div
        className={cn(
          painelInsetCardClass,
          'px-4 py-8 text-center font-inter text-sm text-slate-500',
          className
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className={cn(painelInsetCardClass, 'p-4', className)}>
      {entries.map((entry) => (
        <PainelActivityTimelineItem key={entry.id} entry={entry} variant={variant} />
      ))}
    </ul>
  );
}
